import React, { Dispatch, SetStateAction, useCallback, useContext } from 'react';
import { SecondaryProfilesModalType } from '../Profiles.controller';
import { Network, SecondaryProfileType, SocialOAuthProvider } from '../../../../models/graphql';
import { ExternalSecondaryProfiles_externalSecondaryProfiles } from '../../../../models/legacy-graphql';
import { UseFormReturn } from 'react-hook-form';
import { FormModal } from '../../../../components/containers/FormModal';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { ISecondaryProfileData } from './ConnectSecondarySocialProfilesModal.controller';
import { BoxNotification } from '../../../../components/containers/BoxNotification';
import { SelectAllValidationType } from '../../../../components/actions/SelectAllCheckboxes/SelectAllCheckboxes.view';
import { CheckboxList } from '../../../../components/forms/CheckboxList';
import { SelectAllType } from '../../../../components/forms/CheckboxList/CheckboxList.view';
import { InfoModal } from '../InfoModal/index';
import { InfoModalObject } from '../InfoModal/InfoModal.view';
import { SocialProfileComponentExtendedType } from '../../../../components/socialProfiles/SocialProfile/SocialProfile.view';
import { ValidationMessages } from '../../../../constants/validation-messages';
import { FooterContentTemplate } from '../../../../components/containers/Modal/Modal.view';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { BeaconContext } from '../../../../providers/BeaconProvider';
import {
  ModalFooterTemplate,
  ModalFooterTemplateProps
} from '../../../../components/containers/Modal/ModalFooterTemplates.view';
import { profilesHelpers } from '../Profiles.helpers';

export type ConnectSecondarySocialProfilesModalViewProps = {
  //
  data: SecondaryProfilesModalType;
  formInstance: UseFormReturn;
  disableSelectAll: boolean;
  infoModal: InfoModalObject | null;
  loading: boolean;
  onClose: VoidFunction;
  isPartial: boolean;
  postAsSelect: (profile: ExternalSecondaryProfiles_externalSecondaryProfiles, index: number) => JSX.Element | null;
  selectAllValidation?: SelectAllValidationType;
  setInfoModal: Dispatch<SetStateAction<InfoModalObject | null>>;
  submit: SubmitHandler<ISecondaryProfileData>;
};

type FormModalDataType = {
  provider: SocialOAuthProvider;
  network: Network;
  title: string;
  type: string;
  notification: JSX.Element;
};

export const ConnectSecondarySocialProfilesModalView = (
  props: ConnectSecondarySocialProfilesModalViewProps
): JSX.Element => {
  let output: JSX.Element | null = null;
  const { beacon } = useContext(BeaconContext);
  const { submit, formInstance } = props;

  const hasSelectedProfiles = props.formInstance.getValues('profiles')?.some((p: false | string) => !!p);
  const selectedProfilesCount = Object.values(props.formInstance.getValues('profiles') ?? {})?.filter(p => p)?.length;
  const footerContentTemplate: FooterContentTemplate = useCallback(() => {
    const formValues: ISecondaryProfileData = formInstance.getValues() as ISecondaryProfileData;

    const footerTemplate: ModalFooterTemplateProps = {
      primaryContentPosition: {
        primaryButtonTitle: 'Connect Selected',
        primaryButtonAction: () => submit(formValues),
        primaryButtonColor: ButtonColorsEnum.secondary,
        primaryButtonType: 'button',
        primaryButtonLoading: props.loading,
        primaryButtonDisabled: props.disableSelectAll || !hasSelectedProfiles || props.loading
      },
      secondaryContentPosition: `${selectedProfilesCount} profiles selected`
    };

    return <ModalFooterTemplate {...footerTemplate} />;
  }, [formInstance, hasSelectedProfiles, props.disableSelectAll, props.loading, selectedProfilesCount, submit]);
  const getFormModalJsx = (data: FormModalDataType): JSX.Element => {
    return (
      <FormModal
        closeOnClickOutside={!props.loading}
        closeOnEsc={!props.loading}
        showCloseIcon={!props.loading}
        formInstance={props.formInstance}
        loading={props.loading}
        onClose={props.onClose}
        onSubmit={() => {
          /* Handled in the footer template */
        }}
        size={'large'}
        submitText={'' /* Handled in the footer template */}
        title={data.title}
        footerContent={footerContentTemplate}>
        <>
          <CheckboxList
            {...((props.data.profiles.length > 1
              ? {
                  selectAll: true,
                  checkboxesChildrenGroupName: 'profiles',
                  selectAllSize: 'small',
                  ...(props.selectAllValidation ? { selectAllValidation: props.selectAllValidation } : {})
                }
              : {}) as SelectAllType)}
            disabled={props.loading || props.disableSelectAll}
            formInstance={props.formInstance}
            label={''}>
            {props.data.profiles.map((profile, index) => {
              const socialProfile: SocialProfileComponentExtendedType = {
                id: profile.externalId,
                provider: data.provider,
                network: data.network,
                name: profile.displayName,
                type: data.type
              };
              const isAnotherOrg = !!profile.organizationId && profile.organizationId !== props.data.organization.id;
              const isAnotherWorkspace =
                !!profile.organizationId &&
                profile.organizationId === props.data.organization.id &&
                !!profile.workspaceId &&
                profile.workspaceId !== props.data.workspaceId;
              const wsName = profile.workspaceId
                ? profilesHelpers.getWorkspaceNameById(profile.workspaceId, props.data.organization) || 'another'
                : 'another';
              const socProfile: JSX.Element = (
                <SocialProfile
                  size={'medium'}
                  socialProfile={socialProfile}
                  checkbox
                  {...(props.data.profiles.length === 1 ? { checked: true } : {})}
                  value={profile.externalId}
                  disabled={isAnotherOrg || isAnotherWorkspace}
                  loading={props.loading}
                  {...(isAnotherOrg
                    ? { sublabel: true, sublabelText: 'Already connected to another SmarterQueue Organization' }
                    : {})}
                  {...(isAnotherWorkspace
                    ? {
                        sublabel: true,
                        sublabelText: `Already connected to ${wsName} Workspace within this Organization`
                      }
                    : {})}
                  useFormName={`profiles.${index}`}
                  disconnected={false}
                  label
                  image={profile.photoUrl ? profile.photoUrl : undefined}
                  useFormInstance={props.formInstance}
                  rules={
                    props.data.profiles.length === 1
                      ? { required: ValidationMessages.categoriesSocialProfiles.required }
                      : {}
                  }
                />
              );
              return (
                <div
                  className={'margin-top-small-16 margin-bottom-small-16 grid-x align-justify'}
                  key={profile.externalId}>
                  <div className={'cell auto'}>{socProfile}</div>
                </div>
              );
            })}
          </CheckboxList>
          {props.data.profiles.length ? (
            <>
              <div className={'margin-top-small-24'}>
                <BoxNotification type={'info'}>{data.notification}</BoxNotification>
              </div>
            </>
          ) : null}
        </>
      </FormModal>
    );
  };

  switch (props.data.type) {
    case SecondaryProfileType.COMPANY:
      const LiModalData: FormModalDataType = {
        provider: SocialOAuthProvider.LINKEDIN,
        network: Network.LINKEDIN,
        title: 'Select the companies you want to connect',
        type: 'company',
        notification: (
          <>
            <p className={'no-b-m'}>
              Don&apos;t see your LinkedIn Company above? Make sure you&apos;re currently logged into LinkedIn as an
              admin of that Company page.
            </p>
            <p className={'no-b-m'}>
              <a onClick={() => beacon('article', '725', { type: 'modal' })}>Learn more</a>
            </p>
          </>
        )
      };
      output = getFormModalJsx(LiModalData);
      break;
    case SecondaryProfileType.PAGE:
      const provider =
        props.data.socialOAuth.network === Network.INSTAGRAM
          ? SocialOAuthProvider.INSTAGRAM
          : props.data.socialOAuth.oAuthProvider;
      let notificationText = '';

      if (props.data.socialOAuth.network === Network.INSTAGRAM) {
        notificationText =
          'Don’t see your Instagram Profile above? Make sure you’re currently logged into Facebook as an admin of that Instagram Profile, and that your Instagram profile is a Business/Creator profile connected to a Facebook page. ';
      } else if (provider === SocialOAuthProvider.GOOGLE) {
        notificationText =
          "Don't see your Google Page above? Make sure you're currently logged into Google as admin of that page.";
      } else {
        notificationText =
          "Don't see your Facebook Page above? Make sure you're currently logged into Facebook as admin of that page, and have granted ALL permissions.";
      }

      // @TODO: we need to open in Beacon Instagram: 447 / Facebook: 134
      const readMoreHref =
        provider === SocialOAuthProvider.INSTAGRAM
          ? 'https://help.smarterqueue.com/article/134-why-is-my-facebook-pagegroup-missing-when-i-try-to-add-it'
          : 'https://help.smarterqueue.com/article/134-why-is-my-facebook-pagegroup-missing-when-i-try-to-add-it';

      const pagesModalData: FormModalDataType = {
        provider: provider,
        network: props.data.socialOAuth.network,
        type: 'page',
        title:
          provider === SocialOAuthProvider.INSTAGRAM
            ? 'Select the profile(s) you want to connect'
            : 'Select the pages you want to connect',
        notification: (
          <>
            <p className={'no-b-m'}>{notificationText}</p>
            {provider === SocialOAuthProvider.FACEBOOK || provider === SocialOAuthProvider.INSTAGRAM ? (
              <p className={'no-b-m'}>
                <a href={readMoreHref} target={'_blank'} rel={'noreferrer'}>
                  Read our tips for solving this issue.
                </a>
              </p>
            ) : (
              <p className={'no-b-m'}>
                <a onClick={() => beacon('article', '723', { type: 'modal' })}>Read our tips for solving this issue</a>.
              </p>
            )}
          </>
        )
      };

      output = getFormModalJsx(pagesModalData);
      break;
    case SecondaryProfileType.GROUP:
      const FbGroupsModalData: FormModalDataType = {
        provider: SocialOAuthProvider.FACEBOOK,
        network: Network.FACEBOOK,
        type: 'group',
        title: 'Select groups you want to connect',
        notification: (
          <>
            <p className={'no-b-m'}>
              Don&apos;t see your Facebook Group above? Make sure you&apos;re currently logged into Facebook as admin of
              that group.
            </p>
            <p className={'no-b-m'}>
              <a onClick={() => beacon('article', '723', { type: 'modal' })}>Read our tips for solving this issue</a>.
            </p>
          </>
        )
      };
      output = getFormModalJsx(FbGroupsModalData);
      break;
  }

  if (props.infoModal) {
    output = <InfoModal infoModal={props.infoModal} onClose={props.onClose} setInfoModal={props.setInfoModal} />;
  }

  return <>{output}</>;
};
